import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, EmptyState, Skeleton } from "@/components/ui";
import { api, canManage, type PrivacyRequest } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { intlLocale, msg, t, tn } from "@/lib/i18n";

const KIND: Record<PrivacyRequest["kind"], string> = {
  data_request: msg("Datenauskunft"),
  customer_redact: msg("Löschung Endkunde"),
  shop_redact: msg("Löschung aller Shop-Daten"),
};
const STATUS: Record<PrivacyRequest["status"], { label: string; tone: "warning" | "success" | "neutral" | "danger" | "info" }> = {
  received: { label: msg("Eingegangen"), tone: "info" },
  ready: { label: msg("Auskunft bereit"), tone: "warning" },
  forwarded: { label: msg("Weitergegeben"), tone: "success" },
  done: { label: msg("Erledigt"), tone: "success" },
  failed: { label: msg("Fehlgeschlagen"), tone: "danger" },
};

/** Datenschutz-Anfragen aus den Shops: Auskünfte herunterladen und weitergeben, Löschungen als Nachweis */
export function PrivacyRequestsPage() {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["privacy-requests", tenantId], queryFn: () => api.privacy.list(tenantId), enabled: canManage(role) });
  const forwarded = useMutation({
    mutationFn: (id: string) => api.privacy.forwarded(tenantId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["privacy-requests", tenantId] });
      void qc.invalidateQueries({ queryKey: ["action-items"] });
      toast.success(t("Als weitergegeben markiert"));
    },
  });

  return (
    <div>
      <PageHeader title={msg("Datenschutz-Anfragen")} description={t("Fordert ein Endkunde über euren Shop Auskunft oder Löschung seiner Daten an, kommt die Anfrage hier an. Löschungen erledigt Logiful automatisch; Auskünfte ladet ihr herunter und gebt sie innerhalb von 30 Tagen an den Endkunden weiter.")} />
      <div className="px-4 md:px-8 pb-10">
        {!canManage(role) ? (
          <EmptyState icon={<ShieldCheck className="size-5" />} title={t("Nur für Administratoren")}>{t("Auskünfte enthalten Endkundendaten und sind deshalb nur für Administratoren sichtbar.")}</EmptyState>
        ) : q.isLoading ? (
          <Skeleton className="h-40" />
        ) : !q.data?.length ? (
          <EmptyState icon={<ShieldCheck className="size-5" />} title={t("Keine Anfragen")}>{t("Bisher hat kein Endkunde über euren Shop Auskunft oder Löschung angefragt.")}</EmptyState>
        ) : (
          <Card className="divide-y divide-border">
            {q.data.map((r) => {
              const st = STATUS[r.status];
              return (
                <div key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13.5px] font-medium flex items-center gap-2">
                      {t(KIND[r.kind])}
                      <Badge tone={st.tone}>{t(st.label)}</Badge>
                    </p>
                    <p className="text-[12.5px] text-fg-muted mt-0.5">
                      {[r.customerEmail, tn(r.orders, "{n} Auftrag", "{n} Aufträge"), t("eingegangen {date}", { date: fmtDateTime(r.createdAt) })].filter(Boolean).join(" · ")}
                      {r.status === "ready" && <span className="text-warning"> · {t("Frist bis {date}", { date: new Date(r.dueAt).toLocaleDateString(intlLocale()) })}</span>}
                    </p>
                  </div>
                  {r.kind === "data_request" && (
                    <>
                      <a href={api.privacy.exportUrl(tenantId, r.id)} download>
                        <Button size="sm" variant="secondary" icon={<Download className="size-3.5" />}>{t("Auskunft herunterladen")}</Button>
                      </a>
                      {r.status === "ready" && (
                        <Button size="sm" variant="ghost" disabled={forwarded.isPending} onClick={() => forwarded.mutate(r.id)}>
                          {t("Als weitergegeben markieren")}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </Card>
        )}
      </div>
    </div>
  );
}
